import React, { PropsWithChildren, useCallback } from 'react';
import toast from 'react-hot-toast';

interface IWebShareProps extends PropsWithChildren, ShareData { }

interface IUseWebShareProps extends ShareData { }

enum EShareExceptionsNames {
  INVALID_STATE_ERROR = 'InvalidStateError',
  NOT_ALLOWED_ERROR = 'NotAllowedError',
  ABORT_ERROR = 'AbortError',
  DATA_ERROR = 'DataError',
}

type ShareExceptions = {
  code: number;
  name: EShareExceptionsNames;
  message: string;
};

const isShareExceptions = (
  error: unknown,
): error is ShareExceptions => typeof error === 'object' && error !== null && 'name' in error && Object.values(EShareExceptionsNames).includes(error.name as EShareExceptionsNames);

const useWebShare = ({
  url, title, text, files,
}: IUseWebShareProps) => {
  const activateShare = useCallback(async () => {
    const shareData: ShareData = {
      title, text, url, files,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (!isShareExceptions(error)) {
          toast.error('Failed to share content.');
          return;
        }

        if (error.name === EShareExceptionsNames.ABORT_ERROR) return;

        toast.error(error.message);
      }
    } else {
      toast.error('Web Share not supported in this browser.');
    }
  }, [files, text, title, url]);

  return { activateShare };
};

const WebShare: React.FC<IWebShareProps> = ({
  children, url, files, title = 'Ancestry Pet DNA', text = 'Share your pet results.',
}) => {
  const { activateShare } = useWebShare({
    url, title, text, files,
  });

  return (
    <button type="button" className="btn round-6 share-btn" onClick={activateShare}>
      {children}
    </button>
  );
};

export default WebShare;
